import { useState } from 'react';
import './NpcCard.css';
import type { NpcSheetData } from '../../types/scenario';

/**
 * V7.1 NPC character sheets (2026-06-02) — layered NPC sheet display.
 *
 * Default view shows name + role + sketch — the minimum a player needs
 * to identify the NPC. Clicking "Show stats" expands the full Core
 * Rulebook block (STR/DEX/WIL/PER/INT + skills table with primary/total).
 *
 * Pass `npc: null` to render a graceful placeholder rather than nothing,
 * so a stale npc_inline_refs entry (id removed from scenario.key_npcs
 * but still referenced in the prose) doesn't silently swallow the card.
 *
 * Mook variant (npc.isMook=true) renders with a muted border + a
 * "Combat / Mook" badge so the player visually distinguishes archetype
 * NPCs from named scenario NPCs.
 */

export interface NpcCardProps {
  npc: NpcSheetData | null;
  /** When set, used as the missing-id label (e.g. the requested id). */
  missingId?: string;
  /** Card opens with stats already expanded when true. */
  defaultExpanded?: boolean;
  testidPrefix?: string;
  // 2026-06-05 NPC drawer redaction (Bug Hunt Finding #5). Default true so
  // existing call sites that don't pass this keep their current behavior
  // (no redaction). When false, the card:
  //   1. Returns null entirely if name ends with "(revealed alias)"
  //      — the entry exists in the seed only as a reveal marker.
  //   2. Strips "; Actual: …" / " Actual: …" from the role string.
  //   3. Strips " alias '…' …" patterns from the role string.
  // Both #2 and #3 leave the pre-spoiler portion intact and append a
  // REDACTION_GLYPH so the GM can see something was hidden.
  recognized?: boolean;
}

// Match the dossier exporter's redaction glyph (8 full blocks).
const REDACTION_GLYPH = '█'.repeat(8);

/**
 * Strip villain-reveal spoilers from an NPC role string. Returns the
 * pre-spoiler prefix with a redaction glyph suffix when any spoiler
 * pattern matches; returns the original string untouched otherwise.
 *
 * Patterns covered (case-sensitive; the 1983 seed prose is consistent):
 *   - "; Actual: <X>"          → "; ████████" (Ahmal Ben Gazi)
 *   - " Actual: <X>"           → " ████████"  (defensive — different sep)
 *   - " — alias '<X>' …"       → " — ████████" (Dr. No reveal text)
 */
export function redactNpcRole(role: string): string {
  const semicolonActualIdx = role.indexOf('; Actual:');
  if (semicolonActualIdx !== -1) {
    return role.slice(0, semicolonActualIdx) + `; ${REDACTION_GLYPH}`;
  }
  const spaceActualIdx = role.indexOf(' Actual:');
  if (spaceActualIdx !== -1) {
    return role.slice(0, spaceActualIdx) + ` ${REDACTION_GLYPH}`;
  }
  const aliasIdx = role.search(/\s+(—|--)\s+alias '/);
  if (aliasIdx !== -1) {
    return role.slice(0, aliasIdx) + ` — ${REDACTION_GLYPH}`;
  }
  return role;
}

export function NpcCard({
  npc,
  missingId,
  defaultExpanded = false,
  testidPrefix = 'br-npc-card',
  recognized = true,
}: NpcCardProps) {
  const [expanded, setExpanded] = useState<boolean>(defaultExpanded);

  if (npc === null) {
    return (
      <aside
        className="br-npc-card"
        data-testid={testidPrefix}
        data-state="missing"
      >
        <p className="br-npc-card-missing">
          NPC sheet unavailable
          {missingId !== undefined && missingId.length > 0 ? ` (${missingId})` : ''}
        </p>
      </aside>
    );
  }

  // 2026-06-05 NPC drawer redaction (Bug Hunt Finding #5): when this NPC
  // is not yet fame-recognized AND its name is a "(revealed alias)" reveal
  // marker, the entire card is suppressed. Reveal marker entries exist in
  // the seed only to anchor the reveal moment; rendering them pre-reveal
  // spoils the villain identity.
  if (recognized === false && npc.name.endsWith('(revealed alias)')) {
    return null;
  }

  const roleText = recognized === false ? redactNpcRole(npc.role) : npc.role;

  const statKeys = Object.keys(npc.stats);
  const skillEntries = Object.entries(npc.skills).sort((a, b) =>
    a[0].localeCompare(b[0]),
  );

  return (
    <aside
      className="br-npc-card"
      data-testid={testidPrefix}
      data-npc-id={npc.id}
      data-mook={npc.isMook ? 'true' : 'false'}
    >
      <header className="br-npc-card-header">
        <h3
          className="br-npc-card-name"
          data-testid={`${testidPrefix}-name`}
        >
          {npc.name}
        </h3>
        <span
          className="br-npc-card-role"
          data-testid={`${testidPrefix}-role`}
        >
          {roleText}
        </span>
        {npc.isMook && (
          <span
            className="br-npc-card-badge"
            data-testid={`${testidPrefix}-mook-badge`}
            data-mook-badge="true"
          >
            Combat / Mook
          </span>
        )}
        {!npc.isMook && npc.allegiance !== undefined && (
          <span
            className="br-npc-card-badge"
            data-testid={`${testidPrefix}-allegiance-badge`}
            data-allegiance={npc.allegiance}
          >
            {npc.allegiance}
          </span>
        )}
        {npc.page !== undefined && (
          <span
            className="br-npc-card-page br-label"
            data-testid={`${testidPrefix}-page`}
          >
            p.{npc.page}
          </span>
        )}
      </header>
      {npc.sketch.length > 0 && (
        <p
          className="br-npc-card-sketch"
          data-testid={`${testidPrefix}-sketch`}
        >
          {npc.sketch}
        </p>
      )}
      {(statKeys.length > 0 || skillEntries.length > 0) && (
        <button
          type="button"
          className="br-npc-card-toggle"
          data-testid={`${testidPrefix}-toggle`}
          data-expanded={expanded ? 'true' : 'false'}
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
        >
          {expanded ? 'Hide stats' : 'Show stats'}
        </button>
      )}
      {expanded && (
        <div
          className="br-npc-card-stats"
          data-testid={`${testidPrefix}-stats`}
        >
          {statKeys.length > 0 && (
            <div className="br-npc-card-stats-row">
              {statKeys.map((k) => (
                <span
                  key={k}
                  className="br-npc-card-stat br-label"
                  data-testid={`${testidPrefix}-stat-${k.toLowerCase()}`}
                >
                  <span className="br-npc-card-stat-key">{k}</span>
                  <span className="br-npc-card-stat-value">{npc.stats[k]}</span>
                </span>
              ))}
            </div>
          )}
          {skillEntries.length > 0 && (
            <table
              className="br-npc-card-skills-table"
              data-testid={`${testidPrefix}-skills-table`}
            >
              <thead>
                <tr>
                  <th>Skill</th>
                  <th>Skill Level</th>
                  <th>PC</th>
                </tr>
              </thead>
              <tbody>
                {skillEntries.map(([name, s]) => (
                  <tr key={name}>
                    <td>{name}</td>
                    <td>{s.skill_level}</td>
                    <td>{s.primary_chance}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </aside>
  );
}

export default NpcCard;
